
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Mail, Phone, ArrowLeft, Loader2 } from "lucide-react";
import { authApi } from "@/lib/api";
import { useAuthStore } from "@/lib/store";
import toast from "react-hot-toast";

interface OTPLoginDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

type Step = "identifier" | "otp";

export default function OTPLoginDialog({
  isOpen,
  onClose,
  onSuccess,
}: OTPLoginDialogProps) {
  const navigate = useNavigate();
  const { login } = useAuthStore();
  const [step, setStep] = useState<Step>("identifier");
  const [identifier, setIdentifier] = useState("");
  const [isPhone, setIsPhone] = useState(false);
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [identifierError, setIdentifierError] = useState<string>("");

  const handleClose = () => {
    setStep("identifier");
    setIdentifier("");
    setIsPhone(false);
    setOtp("");
    setOtpSent(false);
    setCountdown(0);
    setIdentifierError("");
    onClose();
  };

  const validateIdentifier = (value: string, isPhone: boolean): { isValid: boolean; error?: string } => {
    if (isPhone) {
      // Remove all non-digits for validation
      const cleaned = value.replace(/\D/g, "");
      
      if (!cleaned) {
        return { isValid: false, error: "Phone number is required" };
      }
      
      // Validate: exactly 10 digits starting with 6, 7, 8, or 9
      if (!/^[6-9]\d{9}$/.test(cleaned)) {
        return { 
          isValid: false, 
          error: "Please enter a valid 10-digit phone number starting with 6, 7, 8, or 9" 
        };
      }
      
      return { isValid: true };
    } else {
      // Enhanced email validation
      if (!value.trim()) {
        return { isValid: false, error: "Email is required" };
      }
      
      // RFC 5322 compliant email regex (simplified but comprehensive)
      const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
      
      if (!emailRegex.test(value.trim())) {
        return { isValid: false, error: "Please enter a valid email address" };
      }
      
      // Additional checks
      if (value.trim().length > 254) {
        return { isValid: false, error: "Email address is too long" };
      }
      
      const parts = value.trim().split("@");
      if (parts[0].length > 64) {
        return { isValid: false, error: "Email address is invalid" };
      }
      
      return { isValid: true };
    }
  };

  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const validation = validateIdentifier(identifier.trim(), isPhone);
    if (!validation.isValid) {
      setIdentifierError(validation.error || "Invalid input");
      toast.error(validation.error || "Invalid input");
      return;
    }
    
    setIdentifierError("");

    setLoading(true);
    try {
      // Normalize phone number for Indian numbers (add +91 prefix for backend)
      let normalizedIdentifier = identifier.trim();
      if (isPhone) {
        const cleaned = normalizedIdentifier.replace(/\D/g, "");
        // Should be exactly 10 digits starting with 6-9
        if (/^[6-9]\d{9}$/.test(cleaned)) {
          normalizedIdentifier = `+91${cleaned}`;
        } else {
          // Invalid format, but let backend handle it
          normalizedIdentifier = `+91${cleaned}`;
        }
      }
      await authApi.sendOTP(normalizedIdentifier, isPhone);
      setOtpSent(true);
      setStep("otp");
      setCountdown(60); // 60 second countdown
      
      // Start countdown
      const interval = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      toast.success(`OTP sent to ${identifier}`);
    } catch (error: any) {
      console.error("Send OTP error:", error);
      toast.error(error.response?.data?.detail || "Failed to send OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!otp.trim() || otp.length !== 6) {
      toast.error("Please enter a valid 6-digit OTP");
      return;
    }

    setLoading(true);
    try {
      // Normalize phone number for Indian numbers (add +91 prefix for backend)
      let normalizedIdentifier = identifier.trim();
      if (isPhone) {
        const cleaned = normalizedIdentifier.replace(/\D/g, "");
        // Should be exactly 10 digits starting with 6-9
        if (/^[6-9]\d{9}$/.test(cleaned)) {
          normalizedIdentifier = `+91${cleaned}`;
        } else {
          // Invalid format, but let backend handle it
          normalizedIdentifier = `+91${cleaned}`;
        }
      }
      const response = await authApi.verifyOTP(normalizedIdentifier, otp.trim(), isPhone);
      if (response.access_token && response.user) {
        login(response.access_token, response.user);
        
        // Sync wishlist if user has items
        if (response.wishlist_count && response.wishlist_count > 0) {
          const { useWishlistStore } = await import("@/lib/store");
          try {
            await useWishlistStore.getState().syncWithBackend(response.access_token);
          } catch (error) {
            console.error("Failed to sync wishlist on login:", error);
          }
        }
        
        toast.success(response.message || "Login successful!");
        handleClose();
        if (onSuccess) {
          onSuccess();
        }
      } else {
        toast.error("Invalid response from server");
      }
    } catch (error: any) {
      console.error("Verify OTP error:", error);
      toast.error(error.response?.data?.detail || "Invalid OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (countdown > 0) {
      toast.error(`Please wait ${countdown} seconds before resending`);
      return;
    }
    await handleSendOTP(new Event("submit") as any);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
        >
          <div className="relative p-6">
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>

            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {step === "identifier" ? "Login or Sign Up" : "Enter OTP"}
              </h2>
              <p className="text-gray-600 text-sm">
                {step === "identifier"
                  ? "Enter your email or phone number to continue"
                  : `We've sent a 6-digit OTP to ${isPhone ? `+91${identifier}` : identifier}`}
              </p>
            </div>

            {step === "identifier" ? (
              <form onSubmit={handleSendOTP} className="space-y-4">
                {/* Login Method Selection - Prominent Tabs */}
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-900 mb-3">
                    Choose Login Method
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setIsPhone(false);
                        setIdentifier("");
                        setIdentifierError("");
                      }}
                      className={`flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all ${
                        !isPhone
                          ? "border-aarsa-gold-600 bg-aarsa-gold-50 shadow-md"
                          : "border-gray-200 bg-white hover:border-gray-300"
                      }`}
                    >
                      <Mail className={`w-8 h-8 mb-2 ${!isPhone ? "text-aarsa-gold-600" : "text-gray-400"}`} />
                      <span className={`text-sm font-semibold ${!isPhone ? "text-aarsa-gold-600" : "text-gray-600"}`}>
                        Email
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setIsPhone(true);
                        setIdentifier("");
                        setIdentifierError("");
                      }}
                      className={`flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all ${
                        isPhone
                          ? "border-aarsa-gold-600 bg-aarsa-gold-50 shadow-md"
                          : "border-gray-200 bg-white hover:border-gray-300"
                      }`}
                    >
                      <Phone className={`w-8 h-8 mb-2 ${isPhone ? "text-aarsa-gold-600" : "text-gray-400"}`} />
                      <span className={`text-sm font-semibold ${isPhone ? "text-aarsa-gold-600" : "text-gray-600"}`}>
                        Phone
                      </span>
                    </button>
                  </div>
                </div>

                {/* Input Field */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {isPhone ? "Phone Number" : "Email Address"}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      {isPhone ? (
                        <Phone className="h-5 w-5 text-aarsa-gold-600" />
                      ) : (
                        <Mail className="h-5 w-5 text-aarsa-gold-600" />
                      )}
                    </div>
                    <input
                      type={isPhone ? "tel" : "email"}
                      value={identifier}
                      onChange={(e) => {
                        let value = e.target.value;
                        
                        // Auto-format phone number for Indian numbers (10 digits only)
                        if (isPhone) {
                          // Remove all non-digits
                          value = value.replace(/\D/g, "");
                          // Limit to 10 digits only
                          value = value.slice(0, 10);
                        }
                        setIdentifier(value);
                        // Clear error on input
                        if (identifierError) {
                          setIdentifierError("");
                        }
                      }}
                      onBlur={() => {
                        if (identifier.trim()) {
                          const validation = validateIdentifier(identifier.trim(), isPhone);
                          if (!validation.isValid) {
                            setIdentifierError(validation.error || "");
                          } else {
                            setIdentifierError("");
                          }
                        }
                      }}
                      placeholder={isPhone ? "9876543210" : "you@example.com"}
                      className={`w-full pl-10 pr-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-aarsa-gold-500 focus:border-aarsa-gold-500 outline-none transition ${
                        identifierError 
                          ? "border-red-300 focus:ring-red-500 focus:border-red-500" 
                          : isPhone 
                            ? "border-aarsa-gold-200 focus:border-aarsa-gold-500" 
                            : "border-aarsa-gold-200 focus:border-aarsa-gold-500"
                      }`}
                      required
                    />
                    {identifierError && (
                      <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                        <span>⚠️</span>
                        {identifierError}
                      </p>
                    )}
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-aarsa-gold-500 text-white py-3 rounded-lg font-medium hover:bg-aarsa-gold-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Sending OTP...
                    </>
                  ) : (
                    "Send OTP"
                  )}
                </button>
              </form>
            ) : (
              <form onSubmit={handleVerifyOTP} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Enter 6-digit OTP
                  </label>
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, "").slice(0, 6);
                      setOtp(value);
                    }}
                    placeholder="000000"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition text-center text-2xl tracking-widest font-mono"
                    maxLength={6}
                    required
                  />
                </div>

                <div className="flex items-center justify-between text-sm">
                  <button
                    type="button"
                    onClick={() => setStep("identifier")}
                    className="text-aarsa-gold-600 hover:text-aarsa-gold-700 flex items-center"
                  >
                    <ArrowLeft className="w-4 h-4 mr-1" />
                    Change {isPhone ? "phone" : "email"}
                  </button>
                  <button
                    type="button"
                    onClick={handleResendOTP}
                    disabled={countdown > 0}
                    className="text-purple-600 hover:text-purple-700 disabled:text-gray-400 disabled:cursor-not-allowed"
                  >
                    {countdown > 0 ? `Resend in ${countdown}s` : "Resend OTP"}
                  </button>
                </div>

                <button
                  type="submit"
                  disabled={loading || otp.length !== 6}
                  className="w-full bg-aarsa-gold-500 text-white py-3 rounded-lg font-medium hover:bg-aarsa-gold-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    "Verify & Continue"
                  )}
                </button>
              </form>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
